import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  Calendar,
  Check,
  Clock,
  XCircle,
  RefreshCw,
  Loader2,
  Filter,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  paymentService,
  type PaymentHistoryItem,
} from "../../services/payment.service";

const STATUS_CONFIG = {
  CREATED: {
    label: "Pending",
    color: "bg-yellow-100 text-yellow-800",
    icon: Clock,
  },
  AUTHORIZED: {
    label: "Authorized",
    color: "bg-blue-100 text-blue-800",
    icon: Clock,
  },
  CAPTURED: {
    label: "Paid",
    color: "bg-green-100 text-green-800",
    icon: Check,
  },
  FAILED: { label: "Failed", color: "bg-red-100 text-red-800", icon: XCircle },
  REFUNDED: {
    label: "Refunded",
    color: "bg-gray-100 text-gray-800",
    icon: RefreshCw,
  },
  PARTIALLY_REFUNDED: {
    label: "Partial Refund",
    color: "bg-orange-100 text-orange-800",
    icon: RefreshCw,
  },
};

export default function PaymentHistoryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await paymentService.getPaymentHistory({
        page,
        limit: 10,
        status: statusFilter || undefined,
      });
      setPayments(response.items);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      console.error("Failed to fetch payments:", error);
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [page, statusFilter]);

  const formatCurrency = (amount: number, currency: string = "INR") => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusConfig = (status: string) => {
    return (
      STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] ||
      STATUS_CONFIG.CREATED
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Payment History
              </h1>
              <p className="text-sm text-gray-500">
                View all your subscription payments
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/subscription")}
            className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            Manage Subscription
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">
                Filter by status:
              </span>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="">All Payments</option>
              <option value="CAPTURED">Paid</option>
              <option value="CREATED">Pending</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>
        </div>

        {/* Payment List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : payments.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No payments found
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {statusFilter
                ? "No payments match the selected filter."
                : "You haven't made any payments yet."}
            </p>
            <button
              onClick={() => navigate("/subscription")}
              className="mt-6 inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors"
            >
              View Subscription Plans
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {payments.map((payment) => {
                const statusConfig = getStatusConfig(payment.status);
                const StatusIcon = statusConfig.icon;

                return (
                  <li
                    key={payment.id}
                    className="p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <CreditCard className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {payment.description || "Subscription Payment"}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="flex items-center text-xs text-gray-500">
                              <Calendar className="mr-1 h-3.5 w-3.5" />
                              {formatDate(payment.createdAt)}
                            </span>
                            {payment.method && (
                              <span className="text-xs text-gray-500 capitalize">
                                via {payment.method}
                              </span>
                            )}
                            {payment.reference && (
                              <span className="text-xs font-mono text-gray-400">
                                {payment.reference}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <span className="text-lg font-semibold text-gray-900">
                          {formatCurrency(payment.amount, payment.currency)}
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusConfig.color}`}
                        >
                          <StatusIcon className="mr-1 h-3.5 w-3.5" />
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-gray-500">
              Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, total)} of{" "}
              {total} payments
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Summary Card */}
        {payments.length > 0 && (
          <div className="mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-6 text-white">
            <h3 className="text-lg font-semibold">Payment Summary</h3>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-indigo-200 text-sm">Total Payments</p>
                <p className="text-2xl font-bold">{total}</p>
              </div>
              <div>
                <p className="text-indigo-200 text-sm">Successful</p>
                <p className="text-2xl font-bold text-green-300">
                  {payments.filter((p) => p.status === "CAPTURED").length}
                </p>
              </div>
              <div>
                <p className="text-indigo-200 text-sm">Pending</p>
                <p className="text-2xl font-bold text-yellow-300">
                  {payments.filter((p) => p.status === "CREATED").length}
                </p>
              </div>
              <div>
                <p className="text-indigo-200 text-sm">Failed</p>
                <p className="text-2xl font-bold text-red-300">
                  {payments.filter((p) => p.status === "FAILED").length}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
